import React, { useEffect, useState } from "react";
import { Spin, Table, Select, Button, Radio, DatePicker, Form } from "antd";
import CardDiv from "modules/common/card-div/CardDiv";
import { useCallingKpi } from "./store/calingKpiStore";
import moment from "moment";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import AscendingLogoGrey from "assets/img/illustrations/AscendingLogoGrey";
import DescendingLogoGrey from "assets/img/illustrations/DescendingLogoGrey";

const { RangePicker } = DatePicker;

const getCurrentMonthDates = () => {
  const startDate = moment().startOf('month').format("YYYY-MM-DD");
  const endDate = moment().endOf('month').format("YYYY-MM-DD");
  return [startDate, endDate];
};

const KpiDashboardHistory = () => {
  const [sortDirection, setSortDirection] = useState(null); // Track sort direction
  const [sortedData, setSortedData] = useState([]); // Store the sorted data
  const today = moment().format("YYYY-MM-DD");

  const defaultFilterValues = {
    pageNumber: 1,
    pageSize: 10,
    agentUserId: null,
    agentRmUserId: null,
    process: null,
    team: null,
    location: null,
    house: null,
    isDownloadReq: null,
    callDate: getCurrentMonthDates(),
  };

  const [filterKpiDashboard, setFilterKpiDashboard] = useState(defaultFilterValues);

  const {
    fetchKpiHistoryData,
    kpiHistoryData,
    kpiHistoryLoading,
    kpiHistoryFilter,
    kpiHistoryFilterLoading,
    fetchKpiHistoryFilter,
  } = useCallingKpi();

  useEffect(() => {
    fetchKpiHistoryData(filterKpiDashboard);
    fetchKpiHistoryFilter();
  }, []);

  useEffect(() => {
    if (kpiHistoryData?.rows_data) {
      setSortedData(kpiHistoryData.rows_data); // Initialize sortedData with fetched data
    }
  }, [kpiHistoryData]);

  const handleTableChange = (pagination) => {
    const updatedFilter = {
      ...filterKpiDashboard,
      pageNumber: pagination.current,
      pageSize: pagination.pageSize,
    };
    setFilterKpiDashboard(updatedFilter);
    fetchKpiHistoryData(updatedFilter);
  };

  const handleSubmitForm = (e) => {
    const updatedFilter = { ...filterKpiDashboard };

    if (!updatedFilter.callDate[0] || !updatedFilter.callDate[1]) {
      updatedFilter.callDate = JSON.stringify([today, today]);
    } else {
      updatedFilter.callDate = JSON.stringify(updatedFilter.callDate);
    }

    if (e?.target?.value) {
      updatedFilter.type = e.target.value;
      setFilterKpiDashboard(updatedFilter);
    }

    const cleanedFilter = (({
      callDate,
      pageNumber,
      pageSize,
      type,
      agentUserId,
      agentRmUserId,
      process,
      team,
      location,
      house,
      isDownloadReq,
    }) => ({
      callDate,
      pageNumber,
      pageSize,
      type,
      agentUserId,
      agentRmUserId,
      process,
      team,
      location,
      house,
      isDownloadReq,
    }))(updatedFilter);
    fetchKpiHistoryData(cleanedFilter);
  };

  const handleClearFilter = () => {
    setFilterKpiDashboard({
      ...defaultFilterValues,
      callDate: getCurrentMonthDates(),
    });
    fetchKpiHistoryData({
      ...defaultFilterValues,
      callDate: JSON.stringify(getCurrentMonthDates()),
    });
  };

const handleSortAttempted = () => {
    let newSortDirection;
    if (sortDirection === null || sortDirection === "dsc") {

      newSortDirection = "asc";
    } else if (sortDirection === "asc") {

      newSortDirection = "dsc";
    }

    setSortDirection(newSortDirection);

    const updatedFilter = {
      ...filterKpiDashboard,
      attempts: "noOfAttempts",
      sortBy: newSortDirection,
    };

    fetchKpiHistoryData(updatedFilter);
  };  
  

  const modifyHeaders = (originalHeaders, sortDirection) =>
    originalHeaders.map((header) => {
      if (header.title.startsWith("Data of date range")) {
        return {
          ...header,
          children: Array.isArray(header.children)
            ? header.children.map((childHeader) => {
                if (childHeader.key === "noOfAttempts") {
                  return {
                    ...childHeader,
                    title: (
                      <OverlayTrigger
                        overlay={
                          <Tooltip id={`tooltip-${childHeader.key}`}>
                            {sortDirection === "asc" ? (
                              <span>Click to sort descending</span>
                            ) : (
                              <span>Click to sort ascending</span>
                            )}
                          </Tooltip>
                        }
                      >
                        <th
                          style={{ cursor: "pointer", textAlign: "center" }}
                          onClick={handleSortAttempted}
                        >
                          {childHeader.title}
                          <div
                            style={{
                              display: "inline-block",
                              paddingLeft: "1rem",
                            }}
                          >
                            <div
                              className={
                                sortDirection === "asc"
                                  ? "opacity-100"
                                  : "opacity-50"
                              }
                            >
                              <AscendingLogoGrey />
                            </div>
                            <div
                              className={
                                sortDirection === "dsc"
                                  ? "opacity-100"
                                  : "opacity-50"
                              }
                            >
                              <DescendingLogoGrey />
                            </div>
                          </div>
                        </th>
                      </OverlayTrigger>
                    )
                  };
                }
                return childHeader;
              })
            : header.children,
        };
      }
      return header;
    });  

  return (
    <CardDiv
      title={
        <div className="d-flex justify-content-between">
          <span>Kpi Dashboard History</span>
          <span className="d-flex gap-2">
            <span className="shadow p-2 text-nowrap text-center">
              Results: {kpiHistoryData.total_row_count}
            </span>
          </span>
        </div>
      }
      className="pt-0 pb-0"
      component={
        <>
          <FilterComponent
            kpiHistoryFilterLoading={kpiHistoryFilterLoading}
            kpiHistoryFilter={kpiHistoryFilter}
            filterKpiDashboard={filterKpiDashboard}
            setFilterKpiDashboard={setFilterKpiDashboard}
            handleClearFilter={handleClearFilter}
            handleSubmitForm={handleSubmitForm}
          />

          <div className="table-responsive">
            <Table
              loading={kpiHistoryLoading}
              columns={modifyHeaders(kpiHistoryData.headers, sortDirection)}
              dataSource={sortedData}
              bordered
              onChange={handleTableChange}
              size="small"
              pagination={{
                current: filterKpiDashboard.pageNumber,
                pageSize: filterKpiDashboard.pageSize,
                total: kpiHistoryData.total_row_count,
                showSizeChanger: true,
              }}
              scroll={{ y: 8 * 40 }}
            />
          </div>
        </>
      }
    />
  );
};

export default KpiDashboardHistory;

const FilterComponent = (props) => {
  const [dateRangeError, setDateRangeError] = useState(false);
  const [dateRangeErrorMessage, setDateRangeErrorMessage] = useState("");
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(false);
  const [rangePickerValue, setRangePickerValue] = useState([
    moment(props.filterKpiDashboard.callDate[0]),
    moment(props.filterKpiDashboard.callDate[1]),
  ]);

  const handleChangeElements = (value, filter_code) => {
    props.setFilterKpiDashboard((prevState) => ({
      ...prevState,
      [filter_code]: value,
    }));
  };

  const handleChange = (dates, filter_code, filter_type) => {
    if (filter_type === "multi_date_picker") {
      // If no dates are selected
      if (!dates) {
        const currentStartDate = moment().format("YYYY-MM-DD");
        const currentEndDate = moment().format("YYYY-MM-DD");

        // Only update if the current state is different
        props.setFilterKpiDashboard((prevState) => {
          if (
            prevState.startDate !== currentStartDate ||
            prevState.endDate !== currentEndDate
          ) {
            return {
              ...prevState,
              callDate: [currentStartDate, currentEndDate],
            };
          }
          return prevState;
        });

        setRangePickerValue([null, null]);
        setDateRangeError(false);
        setDateRangeErrorMessage("");
        setIsSubmitDisabled(false);
        return;
      }

      const startDate = dates[0] ? dates[0].format("YYYY-MM-DD") : null;
      const endDate = dates[1] ? dates[1].format("YYYY-MM-DD") : null;

      // If both startDate and endDate are selected
      if (startDate && endDate) {
        const diffInMonths = moment(endDate).diff(
          moment(startDate),
          "months",
          true
        );

        // Check if the date range exceeds one month
        if (diffInMonths > 1) {
          setDateRangeError(true);
          setDateRangeErrorMessage(
            "The date range should not exceed one month."
          );
          setIsSubmitDisabled(true);
          return;
        }

        setDateRangeError(false);
        setDateRangeErrorMessage("");
        setIsSubmitDisabled(false);

        // Only update the state if the selected dates are different
        props.setFilterKpiDashboard((prevState) => {
          if (
            prevState.startDate !== startDate ||
            prevState.endDate !== endDate
          ) {
            return {
              ...prevState,
              callDate: [startDate, endDate],
            };
          }
          return prevState;
        });

        setRangePickerValue(dates);
      }
    }
  };

  const handleClearDateRange = () => {
    // Clear the RangePicker value
    setRangePickerValue([moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")]);

    // Reset the callDate in filterKpiDashboard to today's date
    props.setFilterKpiDashboard((prevState) => ({
      ...prevState,
      callDate:[moment().format("YYYY-MM-DD"), moment().format("YYYY-MM-DD")] ,
    }));
  };

  return (
    <Spin spinning={props.kpiHistoryFilterLoading}>
      <div className="d-flex flex-wrap gap-3 mb-2">
        {props.kpiHistoryFilter.filters.map((filter) => {
          switch (filter.filter_type) {
            case "code_name_spinner":
              return (
                <div
                  className="d-flex gap-1 align-items-baseline"
                  key={filter.filter_code}
                >
                  <label htmlFor={filter.filter_title}>
                    {filter.filter_title}:
                  </label>
                  <Select
                    size="small"
                    allowClear
                    value={props.filterKpiDashboard[filter.filter_code]}
                    mode={filter.is_single_select ? undefined : "multiple"}
                    options={filter.filter_values
                      ?.map((val) =>
                        val ? { label: val.value, value: val.code } : null
                      )
                      .filter(Boolean)}
                    style={{ minWidth: "200px" }}
                    placeholder={`Select ${filter.filter_title}`}
                    onChange={(value) =>
                      handleChangeElements(value, filter.filter_code)
                    }
                    showSearch
                  />
                </div>
              );
            case "multi_date_picker":
              return (
                <Form.Item
                  size="small"
                  key={filter.filter_code}
                  name={filter.filter_code}
                  label={filter.filter_title}
                  validateStatus={dateRangeError ? "error" : ""}
                  help={dateRangeError ? dateRangeErrorMessage : ""}
                >
                  <RangePicker
                    size="small"
                    placeholder={["Start Date", "End Date"]}
                    format="YYYY-MM-DD"
                    value={rangePickerValue}
                    onChange={(dates) => {
                      handleChange(
                        dates,
                        filter.filter_code,
                        filter.filter_type
                      );
                    }}
                  />
                </Form.Item>
              );
            default:
              return null;
          }
        })}
        <Button
          type="primary"
          disabled={isSubmitDisabled}
          size="small"
          onClick={props.handleSubmitForm}
        >
          Submit
        </Button>
        <Button
          size="small"
          onClick={() => {
            handleClearDateRange();
            props.handleClearFilter();
         
          }}
        >
          Clear
        </Button>
      </div>
    </Spin>
  );
};
